import { FaqHero } from '@components/faq-hero'
import { JsonLd } from '@components/json-ld'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { PageMeta } from '@contentful-components/page-meta'
import { FaqSchemaContext } from '@contentful-components/section-of-faqs/section-of-faqs'
import { SectionResolver } from '@contentful-components/section-resolver'
import type { FaqSectionFragment } from '@graphql/fragments/FaqSectionFragment.graphql'
import { EMPTY_COLLECTION } from '@graphql/placeholders'
import type { StaticPageData } from '@lib/static/page'
import { getContenfulStaticProps } from '@lib/static/static-page'
import { titleToAnchor } from '@lib/utils/faq'
import { PAGE_OPTIONS } from '@lib/utils/page'
import type { NextPage } from 'next'
import type { FAQPage } from 'schema-dts'

export const getStaticProps = getContenfulStaticProps(PAGE_OPTIONS.FAQ.slug)

const Page: NextPage<StaticPageData> = ({ data: initialData, slug }) => {
    // Subscribe to live updates from Contentful
    const data = useContentfulLiveUpdates(initialData)

    if (!data) return null

    const { meta, sections: { items } = EMPTY_COLLECTION } = data

    const faqSections = items.filter(
        (item) => item.type === 'SectionOfFaQs',
    ) as FaqSectionFragment[]

    const links = faqSections.map((faq) => ({
        title: faq.section?.title ?? '',
        anchor: faq.section?.title
            ? titleToAnchor(faq.section.title)
            : faq.sys.id,
    }))

    // Aggregate all FAQ items into a single FAQPage schema to avoid
    // Google Search Console "Duplicate field FAQPage" errors.
    const allFaqItems = faqSections.flatMap(
        (section) => section.faqs?.items ?? [],
    )

    const faqSchema: FAQPage = {
        '@type': 'FAQPage',
        mainEntity: allFaqItems
            .filter((item) => item.question && item.content?.content?.json)
            .map((item) => ({
                '@type': 'Question',
                name: item.question!,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: documentToPlainTextString(
                        item.content!.content!.json,
                    ),
                },
            })),
    }

    return (
        <>
            {meta && <PageMeta id={meta.sys.id} name={slug} meta={meta} />}
            <FaqHero links={links} />
            {allFaqItems.length > 0 && <JsonLd<FAQPage> data={faqSchema} />}
            <FaqSchemaContext.Provider value={false}>
                <SectionResolver sections={items} />
            </FaqSchemaContext.Provider>
        </>
    )
}

export default Page
